import { useState, useEffect, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Search, MapPin, Star, BadgeCheck, Filter, ChevronDown,
  Briefcase, Users, IndianRupee, Clock, X, Wrench, SlidersHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CAProfileDetail } from "@/components/marketplace/CAProfileDetail";
import { CACard, type CAProfile } from "@/components/marketplace/CACard";

const SPECIALIZATIONS = [
  "GST Expert", "Income Tax", "Audit", "Company Registration",
  "Startup Advisory", "Transfer Pricing", "NRI Taxation"
];

const SERVICE_TYPES = [
  "GST Filing", "Income Tax Returns", "Audit & Assurance", "Company Registration",
  "TDS Returns", "Bookkeeping", "Payroll", "Advisory", "Compliance"
];

const CITIES = ["All Cities", "Mumbai", "Delhi", "Chennai", "Ahmedabad", "Jaipur", "Hyderabad", "Kolkata", "Pune", "Bangalore"];

const RATING_OPTIONS = [
  { value: "any", label: "Any Rating" },
  { value: "4", label: "4★ & above" },
  { value: "3", label: "3★ & above" },
  { value: "2", label: "2★ & above" },
];

const EXPERIENCE_OPTIONS = [
  { value: "any", label: "Any Experience" },
  { value: "10", label: "10+ years" },
  { value: "5", label: "5+ years" },
  { value: "3", label: "3+ years" },
  { value: "1", label: "1+ years" },
];

const CAMarketplacePage = () => {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<CAProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("All Cities");
  const [specialization, setSpecialization] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const [minRating, setMinRating] = useState("any");
  const [minExperience, setMinExperience] = useState("any");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sortBy, setSortBy] = useState("rating");
  const [selectedCA, setSelectedCA] = useState<CAProfile | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ca_profiles")
      .select("*")
      .order("avg_rating", { ascending: false });

    if (error) {
      toast({ title: "Error loading profiles", description: error.message, variant: "destructive" });
    } else {
      setProfiles((data as CAProfile[]) || []);
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    let result = [...profiles];

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.firm_name?.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.services.some((s) => s.toLowerCase().includes(q)) ||
        p.specializations.some((s) => s.toLowerCase().includes(q))
      );
    }

    // City filter
    if (city !== "All Cities") {
      result = result.filter((p) => p.city === city);
    }

    // Specialization filter
    if (specialization !== "all") {
      result = result.filter((p) => p.specializations.includes(specialization));
    }

    // Service type filter
    if (serviceType !== "all") {
      result = result.filter((p) =>
        p.services.some((s) => s.toLowerCase().includes(serviceType.toLowerCase()))
      );
    }

    // Rating filter
    if (minRating !== "any") {
      const min = parseFloat(minRating);
      result = result.filter((p) => p.avg_rating >= min);
    }

    // Experience filter
    if (minExperience !== "any") {
      const min = parseInt(minExperience);
      result = result.filter((p) => p.experience_years >= min);
    }

    // Verified only
    if (verifiedOnly) {
      result = result.filter((p) => p.is_verified);
    }

    // Sorting
    if (sortBy === "rating") result.sort((a, b) => b.avg_rating - a.avg_rating);
    else if (sortBy === "experience") result.sort((a, b) => b.experience_years - a.experience_years);
    else if (sortBy === "price_low") result.sort((a, b) => (a.pricing_hourly || 9999) - (b.pricing_hourly || 9999));
    else if (sortBy === "price_high") result.sort((a, b) => (b.pricing_hourly || 0) - (a.pricing_hourly || 0));
    else if (sortBy === "clients") result.sort((a, b) => b.total_clients - a.total_clients);
    else if (sortBy === "reviews") result.sort((a, b) => b.total_reviews - a.total_reviews);

    return result;
  }, [profiles, search, city, specialization, serviceType, minRating, minExperience, verifiedOnly, sortBy]);

  const activeFilters = [
    city !== "All Cities" ? `📍 ${city}` : null,
    specialization !== "all" ? `🎯 ${specialization}` : null,
    serviceType !== "all" ? `🔧 ${serviceType}` : null,
    minRating !== "any" ? `⭐ ${minRating}+` : null,
    minExperience !== "any" ? `📅 ${minExperience}+ yrs` : null,
    verifiedOnly ? "✅ Verified" : null,
  ].filter(Boolean) as string[];

  const clearAllFilters = () => {
    setCity("All Cities");
    setSpecialization("all");
    setServiceType("all");
    setMinRating("any");
    setMinExperience("any");
    setVerifiedOnly(false);
    setSearch("");
  };

  const removeFilter = (f: string) => {
    if (f.startsWith("📍")) setCity("All Cities");
    if (f.startsWith("🎯")) setSpecialization("all");
    if (f.startsWith("🔧")) setServiceType("all");
    if (f.startsWith("⭐")) setMinRating("any");
    if (f.startsWith("📅")) setMinExperience("any");
    if (f.startsWith("✅")) setVerifiedOnly(false);
  };

  if (selectedCA) {
    return <CAProfileDetail ca={selectedCA} onBack={() => setSelectedCA(null)} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-accent" />
            CA Marketplace
          </h1>
          <p className="text-sm text-muted-foreground">Find and connect with verified Chartered Accountants</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            <Users className="h-3 w-3 mr-1" /> {filtered.length} of {profiles.length} CAs
          </Badge>
        </div>
      </div>

      {/* Search Engine */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          {/* Primary Search Row */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, firm, city, or service..."
                className="pl-9"
              />
            </div>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-full md:w-[160px]">
                <MapPin className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={specialization} onValueChange={setSpecialization}>
              <SelectTrigger className="w-full md:w-[180px]">
                <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue placeholder="Specialization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specializations</SelectItem>
                {SPECIALIZATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="experience">Most Experienced</SelectItem>
                <SelectItem value="price_low">Price: Low → High</SelectItem>
                <SelectItem value="price_high">Price: High → Low</SelectItem>
                <SelectItem value="clients">Most Clients</SelectItem>
                <SelectItem value="reviews">Most Reviews</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Advanced Filters Toggle */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground gap-1.5 px-2 h-7">
                <SlidersHorizontal className="h-3 w-3" />
                Advanced Filters
                <ChevronDown className={`h-3 w-3 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-border/30 mt-2">
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger className="w-full">
                    <Wrench className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="Service Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={minRating} onValueChange={setMinRating}>
                  <SelectTrigger className="w-full">
                    <Star className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATING_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={minExperience} onValueChange={setMinExperience}>
                  <SelectTrigger className="w-full">
                    <Clock className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2 px-2">
                  <Switch id="verified-only" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} />
                  <Label htmlFor="verified-only" className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                    <BadgeCheck className="h-3.5 w-3.5 text-accent" /> Verified only
                  </Label>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Active Filters Chips */}
          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Filters:</span>
              {activeFilters.map((f) => (
                <Badge key={f} variant="secondary" className="text-[10px] gap-1">
                  {f}
                  <X className="h-2.5 w-2.5 cursor-pointer hover:text-destructive" onClick={() => removeFilter(f)} />
                </Badge>
              ))}
              <button className="text-[10px] text-accent hover:underline" onClick={clearAllFilters}>
                Clear all
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4 h-[180px]" />
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Search className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-foreground mb-1">No CAs found</h3>
          <p className="text-xs text-muted-foreground mb-3">Try adjusting your search or filters</p>
          {activeFilters.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearAllFilters} className="text-xs">
              <X className="h-3 w-3 mr-1" /> Clear all filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <AnimatePresence>
            {filtered.map((ca, index) => (
              <CACard key={ca.id} ca={ca} rank={index + 1} onSelect={() => setSelectedCA(ca)} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default CAMarketplacePage;
